import type {
  CreateDraftContractFromImportJobMutationOptions,
  CreateDraftContractFromImportJobPayload,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysContractImportJob } from '../../contractImportJob/queryKeys';

import { createDraftContractFromImportJob } from './createDraftContractFromImportJob';

/**
 * Starts a draft contract for the create form from a finished import job.
 * The job is done afterwards, so the import jobs are refetched; an unsaved
 * draft is not part of any contract list.
 *
 * @param options - Additional mutation options.
 * @returns The mutation.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/create_draft_from_import_job_contracts_drafts_from_import_job_post
 */
export const useMutationCreateDraftContractFromImportJob = (
  options?: CreateDraftContractFromImportJobMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (payload: CreateDraftContractFromImportJobPayload) =>
      createDraftContractFromImportJob(payload),
    async onSuccess(data, variables, onMutateResult, context) {
      await options?.onSuccess?.(data, variables, onMutateResult, context);

      return queryClient.invalidateQueries({
        queryKey: [queryKeysContractImportJob.base()],
      });
    },
  });
};
